import { Anchor, Badge, Card, Group, Loader, SegmentedControl, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminDecisions, type AdminDecision } from '../lib/api.js';

function relTime(ms: number): string {
  const min = Math.round((Date.now() - ms) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} h`;
  return `vor ${Math.round(h / 24)} d`;
}

const CATEGORY_LABEL: Record<string, string> = {
  lage: 'Lage', preis: 'Preis', groesse: 'Größe', zimmer: 'Zimmer', zustand: 'Zustand',
  aussenbereich: 'Außenbereich', umgebung: 'Umgebung', pendel: 'Pendel', vibe: 'Vibe',
  duplikat: 'Duplikat', inseratweg: 'Inserat weg', sonstiges: 'Sonstiges',
};
const OBJ_LABEL: Record<string, string> = { wohnung: 'Wohnung', haus: 'Haus', grundstueck: 'Grundstück' };

function DecisionBadge({ d }: { d: AdminDecision }): JSX.Element {
  if (d.eventType === 'partner_review') return <Badge color="green" variant="light" tt="none">🔥 Hot → Partner</Badge>;
  if (d.eventType === 'partner_approved') return <Badge color="blue" variant="light" tt="none">🤝 Partner-Freigabe</Badge>;
  const cat = d.category ? ` · ${CATEGORY_LABEL[d.category] ?? d.category}` : '';
  return <Badge color="red" variant="light" tt="none">👈 Pass{cat}</Badge>;
}

const FILTERS = [
  { label: 'Alle', value: 'all' },
  { label: 'Pass', value: 'rejected' },
  { label: 'Hot', value: 'partner_review' },
  { label: 'Partner-Freigabe', value: 'partner_approved' },
];

export function AdminDecisions(): JSX.Element {
  const [type, setType] = useState('all');
  const { data, isLoading } = useQuery({
    queryKey: ['admin-decisions', type],
    queryFn: () => getAdminDecisions({ type: type === 'all' ? undefined : type, limit: 300 }),
  });
  const decisions = data?.decisions ?? [];

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Text component="h1" fw={800} style={{ fontSize: '1.6rem' }}>
          Entscheidungen
        </Text>
        <Text c="dimmed" size="sm">
          Alle Swipe-Bewertungen über alle Konten — wer hat wann welche Immobilie wie bewertet.
        </Text>
      </Stack>

      <SegmentedControl value={type} onChange={setType} data={FILTERS} size="sm" radius="md" />

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : decisions.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Noch keine Entscheidungen.
        </Text>
      ) : (
        <Stack gap="xs">
          {decisions.map((d) => (
            <Card key={d.id} withBorder padding="sm" radius="md">
              <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                <Stack gap={3} style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="xs" wrap="wrap">
                    <DecisionBadge d={d} />
                    <Text size="xs" c="dimmed">{relTime(d.occurredAt)}</Text>
                  </Group>
                  <Text size="sm" fw={600} lineClamp={1}>
                    {OBJ_LABEL[d.objektTyp ?? ''] ?? 'Objekt'}
                    {d.ort ? ` · ${d.ort}` : ''}
                    {d.portal ? ` · ${d.portal}` : ''}
                  </Text>
                  {d.note ? (
                    <Text size="xs" c="dimmed" lineClamp={2}>„{d.note}"</Text>
                  ) : d.title ? (
                    <Text size="xs" c="dimmed" lineClamp={1}>{d.title}</Text>
                  ) : null}
                </Stack>
                <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                  <Text size="xs" fw={600}>{d.userName ?? '—'}</Text>
                  {d.login ? <Text size="xs" c="dimmed">{d.login}</Text> : null}
                  {d.url ? (
                    <Anchor href={d.url} target="_blank" rel="noopener noreferrer" size="xs">Inserat ↗</Anchor>
                  ) : (
                    <Anchor component={Link} to={`/objects/${d.objectId}`} size="xs">Objekt</Anchor>
                  )}
                </Stack>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
